
import { useState, useEffect, useCallback, useRef } from 'react';
import { AdminState, AdminAction } from '@/types/adminTypes';

type ToastFunction = {
  toast: (props: { title: string; description: string; variant?: "default" | "destructive" }) => void;
};

export const useAdminSync = (
  adminState: AdminState,
  refreshAttendees: () => void,
  refreshAdminData: () => void,
  generateDemoData: null,
  resetData: () => void,
  recordAction: (actionType: AdminAction['actionType'], adminName: string, details?: string, attendeeCode?: string) => void,
  adminName: string,
  { toast }: ToastFunction,
  usingSupabase: boolean = false,
  isProduction: boolean = true
) => {
  const [lastSyncTime, setLastSyncTime] = useState<string>('');
  const isSyncing = useRef(false);
  const lastAdminStateRef = useRef<string>('');
  const syncTimeoutRef = useRef<number | null>(null);
  
  // Initialize last sync time from admin state
  useEffect(() => {
    if (adminState.lastSync && !lastSyncTime) {
      setLastSyncTime(new Date(adminState.lastSync).toLocaleTimeString('vi-VN'));
      lastAdminStateRef.current = adminState.lastSync;
    }
  }, [adminState.lastSync, lastSyncTime]);
  
  // Update last sync time when admin state changes
  useEffect(() => {
    if (adminState.lastSync && adminState.lastSync !== lastAdminStateRef.current) {
      const timeDifference = Math.abs(
        new Date(adminState.lastSync).getTime() - 
        new Date(lastAdminStateRef.current || '').getTime()
      );
      
      // Only update if the time difference is significant (> 5 seconds)
      if (timeDifference > 5000) {
        const newTime = new Date(adminState.lastSync).toLocaleTimeString('vi-VN');
        if (newTime !== lastSyncTime) {
          console.log('Updating sync time silently - NO PAGE REFRESH');
          setLastSyncTime(newTime);
          lastAdminStateRef.current = adminState.lastSync;
        }
      }
    }
  }, [adminState.lastSync, lastSyncTime]);
  
  // Clean up timeouts on unmount
  useEffect(() => {
    return () => {
      if (syncTimeoutRef.current !== null) {
        clearTimeout(syncTimeoutRef.current);
      }
    };
  }, []);
  
  // Manual sync handler with optimized data refresh
  const handleManualSync = useCallback(() => {
    if (isSyncing.current) return;
    
    console.log('Starting silent manual sync - NO PAGE REFRESH');
    isSyncing.current = true;
    
    const now = new Date().toISOString();
    const formattedTime = new Date(now).toLocaleTimeString('vi-VN');
    
    // No toast for starting sync - reduce notification noise
    
    // Small delay before refreshing data to avoid UI flicker
    setTimeout(() => {
      Promise.all([
        refreshAttendees(),
        refreshAdminData()
      ]).then(() => {
        console.log('Silent manual sync completed - NO PAGE REFRESH');
        setLastSyncTime(formattedTime);
        lastAdminStateRef.current = now;
        
        recordAction('other', adminName, 'Manual data synchronization');
        
        // Use a custom event to notify components about the sync
        window.dispatchEvent(new CustomEvent('data-sync-completed', {
          detail: { timestamp: formattedTime }
        }));
        
        syncTimeoutRef.current = window.setTimeout(() => {
          isSyncing.current = false;
          syncTimeoutRef.current = null;
        }, 1000);
      }).catch(err => {
        console.error("Sync error:", err);
        
        toast({
          title: "Lỗi đồng bộ dữ liệu",
          description: "Đã xảy ra lỗi khi đồng bộ. Vui lòng thử lại sau.",
          variant: "destructive"
        });
        
        isSyncing.current = false;
      });
    }, 300);
  }, [refreshAttendees, refreshAdminData, recordAction, adminName, toast]);
  
  // Reset data handler
  const handleResetData = useCallback(() => {
    if (isSyncing.current) return;
    isSyncing.current = true;
    
    toast({
      title: "Đang xóa dữ liệu...",
      description: "Vui lòng đợi trong giây lát"
    });
    
    setTimeout(() => {
      resetData();
      
      recordAction('reset', adminName, 'Reset all system data');
      
      syncTimeoutRef.current = window.setTimeout(() => {
        handleManualSync();
        syncTimeoutRef.current = null;
      }, 1000);
    }, 300);
  }, [resetData, recordAction, adminName, handleManualSync, toast]);
  
  // Listen for data sync events
  useEffect(() => {
    const handleSyncCompleted = (event: CustomEvent) => {
      console.log("Data sync completed - Updating UI without page refresh", event.detail);
      if (event.detail?.timestamp) {
        setLastSyncTime(event.detail.timestamp);
      }
    };
    
    window.addEventListener('data-sync-completed', handleSyncCompleted as EventListener);
    
    return () => {
      window.removeEventListener('data-sync-completed', handleSyncCompleted as EventListener);
    };
  }, []);
  
  return {
    lastSyncTime,
    handleManualSync,
    handleResetData,
    usingSupabase
  };
};
